import { useTranslation } from 'react-i18next';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import { Plus, Loader2, Trash2, Pencil } from 'lucide-react';
import { StepIndicator } from './StepIndicator';
import { VARIATION_TYPES } from './constants';
import type { AssociationGroup, VariationType } from '@/lib/types';

interface SelectGroupStepProps {
  groups: AssociationGroup[] | undefined;
  isLoadingGroups: boolean;
  groupToDelete: AssociationGroup | null;
  isDeleting: boolean;
  variationType: VariationType;
  onSelectGroup: (group: AssociationGroup) => void;
  onCreateNewGroup: () => void;
  onEditGroup: (group: AssociationGroup) => void;
  onDeleteGroup: (group: AssociationGroup) => void;
  onSetGroupToDelete: (group: AssociationGroup | null) => void;
  onClose: () => void;
}

export function SelectGroupStep({
  groups,
  isLoadingGroups,
  groupToDelete,
  isDeleting,
  variationType,
  onSelectGroup,
  onCreateNewGroup,
  onEditGroup,
  onDeleteGroup,
  onSetGroupToDelete,
  onClose,
}: SelectGroupStepProps) {
  const { t } = useTranslation();

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('associations.selectGroup')}</DialogTitle>
        <DialogDescription>{t('associations.selectGroupDescription')}</DialogDescription>
      </DialogHeader>

      <StepIndicator step="select_group" variationType={variationType} />

      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        <Button
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={onCreateNewGroup}
        >
          <Plus className="size-4" />
          {t('associations.createNewGroup')}
        </Button>

        {isLoadingGroups ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : groups && groups.length > 0 ? (
          <div className="space-y-2">
            {groups.map((group) => {
              const variationConfig = VARIATION_TYPES.find((v) => v.value === group.variationType);
              const isDeleteTarget = groupToDelete?.groupId === group.groupId;

              return (
                <div
                  key={group.groupId}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <button
                    onClick={() => onSelectGroup(group)}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{group.name}</p>
                        {group.description && (
                          <p className="text-sm text-muted-foreground">{group.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="gap-1">
                          {variationConfig?.icon}
                          {t(variationConfig?.labelKey || 'associations.variationTypes.related')}
                        </Badge>
                        <Badge variant="outline">
                          {group.items?.length || group.products?.length || 0} {t('products.productsCount')}
                        </Badge>
                      </div>
                    </div>
                  </button>

                  {isDeleteTarget ? (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDeleteGroup(group)}
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          t('common.confirm')
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onSetGroupToDelete(null)}
                        disabled={isDeleting}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditGroup(group);
                        }}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSetGroupToDelete(group);
                        }}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-4">
            {t('associations.noGroups')}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </DialogFooter>
    </>
  );
}
